import { memo, StrictMode } from 'react';
import { bool, string } from 'prop-types';
import { getEnv } from '../environment';
import { Banner } from './components/Banners/Banner';
import NMFHeader from './components/Header/NMFHeader';
import GlobalNavInternalContext from './context/GlobalNavInternalContext';
import OptimizelyProvider from './context/OptimizelyProvider';
import { useBootstrapLD } from './hooks/useBootstrapLD';
import { useNmfRedirect } from './hooks/useNmfRedirect';
import { useNmfTimer } from './hooks/useNmfTimer';

type NewMemberNavTemplateProps = {
  env?: string;
  step: string;
  isEditCampaignOpen: boolean;
};

// Rendered inside the OptimizelyProvider so the timer hooks can resolve the deprecation flag.
const NewMemberNavTemplateContent = ({
  env: envName = '',
  step,
  isEditCampaignOpen,
}: NewMemberNavTemplateProps) => {
  const env = getEnv(envName);
  const {
    userId,
    endTime,
    lastVisitedCustomizeQuestionId,
    timerType,
    timerSource,
    newMemberState,
    loading: timerLoading,
    isTimerDeprecated,
  } = useNmfTimer(env);

  const {
    downloadMobileAppWebBannerFF,
    downloadMobileAppWebBannerReady,
    mobileAppWebBannerUniversalLinksFF,
    mobileAppWebBannerUniversalLinksReady,
    mobileAppWebBannerLoyaltyProgramFF,
    mobileAppWebBannerLoyaltyProgramReady,
    ordersCtaFF,
    ordersCtaReady,
    webRatingsAndReviewInGlobalNavDropDownFF,
    webRatingsAndReviewInGlobalNavDropDownReady,
    ccToastNotificationFF,
    ccToastNotificationReady,
  } = useBootstrapLD(env.name, userId);

  const contextValue = {
    env,
    flags: {
      downloadMobileAppWebBannerFF,
      downloadMobileAppWebBannerReady,
      mobileAppWebBannerUniversalLinksFF,
      mobileAppWebBannerUniversalLinksReady,
      mobileAppWebBannerLoyaltyProgramFF,
      mobileAppWebBannerLoyaltyProgramReady,
      ordersCtaFF,
      ordersCtaReady,
      webRatingsAndReviewInGlobalNavDropDownFF,
      webRatingsAndReviewInGlobalNavDropDownReady,
      ccToastNotificationFF,
      ccToastNotificationReady,
    },
  };

  useNmfRedirect(
    newMemberState,
    lastVisitedCustomizeQuestionId,
    env.REACT_APP_BASE_URL,
    timerType,
    isTimerDeprecated,
  );

  if (timerLoading || (!isTimerDeprecated && !timerSource)) {
    return null;
  }

  return (
    <StrictMode>
      <GlobalNavInternalContext.Provider value={contextValue}>
        <Banner />
        <NMFHeader
          step={step}
          isEditCampaignOpen={isEditCampaignOpen}
          endTime={endTime}
          timerSource={isTimerDeprecated ? undefined : timerSource}
        />
      </GlobalNavInternalContext.Provider>
    </StrictMode>
  );
};

const NewMemberNavTemplate = (props: NewMemberNavTemplateProps) => (
  <OptimizelyProvider>
    <NewMemberNavTemplateContent {...props} />
  </OptimizelyProvider>
);

NewMemberNavTemplate.propTypes = {
  env: string.isRequired,
  step: string.isRequired,
  isEditCampaignOpen: bool,
};

export default memo(NewMemberNavTemplate);
